import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown, Download } from 'lucide-react'

export interface ExportMenuButtonProps {
  disabled?: boolean
  onCsv: () => void
  onExcel: () => void
  label?: string
}

/**
 * Compact CSV / Excel export control (shared pattern for analytics tables).
 * Primary label defaults to "Export"; menu items "Export as CSV / Excel".
 */
export function ExportMenuButton({
  disabled,
  onCsv,
  onExcel,
  label = 'Export',
}: ExportMenuButtonProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className={[
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium',
          'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
        ].join(' ')}
      >
        <Download className="w-3.5 h-3.5" />
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && !disabled && (
        <div
          className="absolute right-0 mt-1 py-1 min-w-[132px] rounded-lg shadow-lg z-50 border border-slate-200 bg-white"
        >
          <button
            type="button"
            onClick={() => {
              onCsv()
              setOpen(false)
            }}
            className="block w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
          >
            Export as CSV
          </button>
          <button
            type="button"
            onClick={() => {
              onExcel()
              setOpen(false)
            }}
            className="block w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
          >
            Export as Excel
          </button>
        </div>
      )}
    </div>
  )
}
